// Samples (spec §11, §12.4; 05 §5.7): copy and pure helpers. The server
// selects samples, fits the calibration and finds order warnings; these only
// word, navigate and preview the rubric range.

export const SAMPLES_COPY = Object.freeze({
  title: 'Samples',
  intro:
    'Mark at least 3 sample answers for each question. JeddAI uses them to match your standard.',
  progress: (done, total) => `${done} of ${total} marked`,
  // Waiting title + count: ResponsesStep COPY.markingMfm / markingMfmPct
  // (spec §9.3), with a progress bar.
  waitingBody: 'Your samples open as soon as it has marked and ranked them.',
  addSample: 'Mark another sample',
  optional: '(Optional)',
  removeSample: 'Remove sample',
  rubricHead: 'Rubric grades',
  rubricHint: 'Grade each criterion, then give the mark.',
  guideHead: 'Marking guide',
  guideHint: 'Choose the mark that best fits this answer.',
  mark: 'Mark',
  yourMark: (mark, max) => `Your mark: ${mark ?? '–'} / ${max}`,
  overMax: 'Above the maximum',
  mismatch: (mark, lo, hi) =>
    `${mark} is outside the range for these grades (${lo}–${hi}). Check the grades or the mark.`,
  nextSample: 'Next sample',
  nextQuestion: 'Next question',
  nextUnmarked: 'Next unmarked sample',
  allDone: 'All samples done',
  skip: 'Skip',
  markAll: (n) => `Mark all ${n} scripts`,
  viewResults: 'View results',
  orderTitle: (label) => `Your marks don't follow JeddAI's order on ${label}`,
  orderBody: (higher, lower) =>
    `You gave ${higher} a higher mark than ${lower}, which JeddAI ranked above it. Check both, or add another sample so JeddAI has more to go on.`,
  orderGo: 'Calibrate anyway',
  orderBack: 'Add another sample',
  noMore: 'There are no more responses to sample.',
  saveFirst: "Your last sample mark wasn't saved. Check it and try again.",
  saveFirstOn: (label) =>
    `Your mark on ${label || 'a sample'} wasn't saved. Check it and try again.`,
  handwritten: 'Handwritten',
  typed: 'Typed',
});

/** The server lowers the required count when fewer scripts are available. */
export const sampleIntro = (questions) => {
  const counts = (questions || []).map((question) => question.minRequired)
    .filter((count) => Number.isInteger(count) && count > 0);
  if (!counts.length || counts.every((count) => count === 3)) return SAMPLES_COPY.intro;
  if (!counts.every((count) => count === counts[0]))
    return 'Mark the required samples for each question. JeddAI uses them to match your standard.';
  const count = counts[0];
  return `Mark at least ${count} sample ${count === 1 ? 'answer' : 'answers'} for each question. JeddAI uses them to match your standard.`;
};

/**
 * Expected range for the selected rubric levels (spec §12.4): [Σ minPoints,
 * Σ maxPoints]. null until every criterion has a level.
 */
export const gradeRange = (rubric, picks) => {
  const criteria = Array.isArray(rubric?.criteria) ? rubric.criteria : [];
  if (criteria.length === 0) return null;
  const chosen = new Map(
    (Array.isArray(picks) ? picks : []).map((pick) => [
      pick.criterionId,
      pick.levelId,
    ])
  );
  let lo = 0;
  let hi = 0;
  for (const criterion of criteria) {
    const level = (criterion.levels || []).find(
      (item) => item.levelId === chosen.get(criterion.criterionId)
    );
    if (!level) return null;
    lo += level.minPoints ?? 0;
    hi += level.maxPoints ?? 0;
  }
  return { lo, hi };
};

/** The mismatch line, or null when in range / not checkable. */
export const mismatchText = (rubric, picks, mark) => {
  if (mark === null || mark === undefined || Number.isNaN(mark)) return null;
  const range = gradeRange(rubric, picks);
  if (!range || (mark >= range.lo && mark <= range.hi)) return null;
  return SAMPLES_COPY.mismatch(mark, range.lo, range.hi);
};

/** Every sample in tab order: [{questionIndex, sampleIndex, sample, question}]. */
export const flatSamples = (questions) =>
  (Array.isArray(questions) ? questions : []).flatMap((question, qi) =>
    (question.samples || []).map((sample, si) => ({
      questionIndex: qi,
      sampleIndex: si,
      question,
      sample,
    }))
  );

/** Where "Next" goes from a position, and its label (spec §11 "Next"). */
export const nextTarget = (questions, position) => {
  const all = flatSamples(questions);
  if (all.length === 0) return { label: SAMPLES_COPY.allDone, target: null };
  if (all.every((item) => item.sample.done)) {
    return { label: SAMPLES_COPY.allDone, target: null };
  }
  const index = all.findIndex(
    (item) =>
      item.questionIndex === position.questionIndex &&
      item.sampleIndex === position.sampleIndex
  );
  const next = all[index + 1];
  if (next && next.questionIndex === position.questionIndex) {
    return { label: SAMPLES_COPY.nextSample, target: next };
  }
  if (next) return { label: SAMPLES_COPY.nextQuestion, target: next };
  const unmarked = all.find((item) => !item.sample.done);
  return { label: SAMPLES_COPY.nextUnmarked, target: unmarked || null };
};

/** Questions whose minimum isn't met (for the Continue message). */
export const incompleteLabels = (questions) =>
  (Array.isArray(questions) ? questions : [])
    .filter((question) => !question.minDone)
    .map((question) => question.label);
